/**
 * HTML Sanitizer — removes potentially dangerous tags/attributes
 * to prevent XSS attacks from user-generated or CMS content.
 */

const ALLOWED_TAGS = new Set([
  'p', 'br', 'b', 'i', 'u', 'em', 'strong', 'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'img', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'span', 'div', 'section', 'article', 'hr', 'sup', 'sub', 'mark',
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
  '*': new Set(['class', 'id', 'style']),
};

/**
 * Sanitize an HTML string by stripping dangerous elements and attributes.
 * Uses the browser's built-in DOMParser for robust parsing.
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';

  // Use DOMParser for server-safe parsing
  if (typeof window === 'undefined') return dirty;

  const parser = new DOMParser();
  const doc = parser.parseFromString(dirty, 'text/html');

  const clean = sanitizeNode(doc.body);
  return clean.innerHTML;
}

function sanitizeNode(node: Element): Element {
  const children = Array.from(node.childNodes);

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;

    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tagName = el.tagName.toLowerCase();

      // Remove script, style, iframe, object, embed, form, input
      if (!ALLOWED_TAGS.has(tagName)) {
        // Keep text content but remove the element itself
        const fragment = document.createDocumentFragment();
        while (el.firstChild) {
          fragment.appendChild(el.firstChild);
        }
        node.replaceChild(fragment, el);
        continue;
      }

      // Strip dangerous attributes
      const allowedForTag = ALLOWED_ATTRS[tagName] || new Set();
      const allowedGlobal = ALLOWED_ATTRS['*'] || new Set();

      const attrs = Array.from(el.attributes);
      for (const attr of attrs) {
        const name = attr.name.toLowerCase();

        // Remove event handlers and javascript: URLs
        if (name.startsWith('on') || attr.value.toLowerCase().includes('javascript:')) {
          el.removeAttribute(attr.name);
          continue;
        }

        if (!allowedForTag.has(name) && !allowedGlobal.has(name)) {
          el.removeAttribute(attr.name);
        }
      }

      // Force safe link attributes
      if (tagName === 'a') {
        el.setAttribute('rel', 'noopener noreferrer');
      }

      // Recurse into children
      sanitizeNode(el);
    } else {
      // Remove comments and other node types
      node.removeChild(child);
    }
  }

  return node;
}
